/**
 * RoamerAIConcierge.ts — Engine #10: the Roamer AI Concierge™.
 *
 * The user's trusted digital travel companion — an experienced remote worker,
 * not a generic chatbot. Orchestration only:
 *  • Context arrives via an injectable ContextGatherer (wired by adapters to
 *    Core/Location/Intelligence/Mission/Fusion/Learning/Community/Predictive/
 *    Knowledge/Score) — the concierge never hard-imports other engines.
 *  • Intent classification and per-intent reasoning are pure and
 *    deterministic (intents.ts / handlers.ts).
 *  • Actions are REQUESTED through an injectable dispatcher (Roamer Core™),
 *    never executed directly against other engines.
 *  • Session memory is a bounded transcript; long-term improvement flows
 *    through an injectable learning bridge. Both respect memoryEnabled —
 *    when off, nothing is recorded or emitted.
 *  • Safety: handlers never fabricate; provenance is explicit; user-facing
 *    text never exposes internal engine architecture.
 */

import { classifyIntent } from './intents';
import { INTENT_HANDLERS } from './handlers';
import {
  DEFAULT_CONCIERGE_SETTINGS,
  type ConciergeActionRequest,
  type ConciergeContext,
  type ConciergeResponse,
  type ConciergeSettings,
  type ConciergeTurn,
} from './models';

const STORAGE_KEY = 'nomad:concierge:v1';
const MAX_TURNS = 100;

/** Supplies the reasoning context. Injectable — wired by adapters, not here. */
export type ContextGatherer = (
  now: number,
) => Promise<ConciergeContext> | ConciergeContext;

/** Routes proposed actions through Roamer Core™ — never direct engine calls. */
export type ActionDispatcher = (
  action: ConciergeActionRequest,
) => Promise<boolean> | boolean;

/** Bridge to long-term preference learning (no-op unless wired + memory on). */
export type LearningBridge = (event: {
  intent: string;
  query: string;
  at: number;
}) => void;

interface PersistedState {
  turns: ConciergeTurn[];
  settings: ConciergeSettings;
}

export class RoamerAIConcierge {
  private gatherer: ContextGatherer = () => ({});
  private dispatcher: ActionDispatcher | null = null;
  private learningBridge: LearningBridge | null = null;
  private turns: ConciergeTurn[] = [];
  private settings: ConciergeSettings = { ...DEFAULT_CONCIERGE_SETTINGS };

  constructor() {
    this.load();
  }

  // ── Wiring (adapters, not direct engine imports) ────────────────────────────

  setContextGatherer(gatherer: ContextGatherer): void {
    this.gatherer = gatherer;
  }

  setActionDispatcher(dispatcher: ActionDispatcher): void {
    this.dispatcher = dispatcher;
  }

  setLearningBridge(bridge: LearningBridge): void {
    this.learningBridge = bridge;
  }

  // ── Conversation ────────────────────────────────────────────────────────────

  /**
   * Answer a user query: gather context → classify intent → reason → respond.
   * Deterministic for a given (context, query, now).
   */
  async ask(query: string, now: number = Date.now()): Promise<ConciergeResponse> {
    let context: ConciergeContext = {};
    try {
      // Deep snapshot at the read boundary — handlers can never mutate
      // caller-owned engine data, and no side effects leak across turns.
      context = structuredClone(await this.gatherer(now));
    } catch {
      // a failing source must never break the conversation — honest fallbacks
    }
    const intent = classifyIntent(query);
    const handled = INTENT_HANDLERS[intent](context, query, now);
    const response: ConciergeResponse = { intent, at: now, ...handled };

    if (this.settings.memoryEnabled) {
      this.turns.push({ role: 'user', text: query, intent, at: now });
      this.turns.push({ role: 'concierge', text: response.reply, intent, at: now });
      if (this.turns.length > MAX_TURNS) this.turns = this.turns.slice(-MAX_TURNS);
      this.persist();
      try {
        this.learningBridge?.({ intent, query, at: now });
      } catch {
        /* learning is best-effort — never breaks the reply */
      }
    }
    return structuredClone(response);
  }

  /** Request a platform action — routed through Core via the dispatcher. */
  async requestAction(action: ConciergeActionRequest): Promise<boolean> {
    if (!this.dispatcher) return false;
    try {
      return await this.dispatcher(structuredClone(action));
    } catch {
      return false;
    }
  }

  // ── Session memory & privacy ────────────────────────────────────────────────

  getTranscript(): ConciergeTurn[] {
    return structuredClone(this.turns);
  }

  getSettings(): ConciergeSettings {
    return { ...this.settings };
  }

  /** Turning memory off also erases the existing transcript (privacy-first). */
  updateSettings(patch: Partial<ConciergeSettings>): ConciergeSettings {
    this.settings = { ...this.settings, ...patch };
    if (this.settings.memoryEnabled === false) this.turns = [];
    this.persist();
    return this.getSettings();
  }

  clearConversation(): void {
    this.turns = [];
    this.persist();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = { turns: this.turns, settings: this.settings };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  private load(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Partial<PersistedState> | null;
      if (state == null || typeof state !== 'object') return;
      this.settings = {
        memoryEnabled: state.settings?.memoryEnabled === false ? false : true,
      };
      this.turns = this.settings.memoryEnabled && Array.isArray(state.turns)
        ? state.turns
            .filter(
              (t): t is ConciergeTurn =>
                t != null && typeof t === 'object' &&
                (t.role === 'user' || t.role === 'concierge') &&
                typeof t.text === 'string' && typeof t.at === 'number',
            )
            .slice(-MAX_TURNS)
        : [];
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton (context gatherer + dispatcher wired by adapters). */
export const roamerAIConcierge = new RoamerAIConcierge();
